<script setup>
import {computed, markRaw, reactive, ref, watch} from "vue";
import {wTrans} from "laravel-vue-i18n";

import CalendarSelector from "./common/CalendarSelector.vue";
import TimeSlotPicker from "./personal/TimeSlotPicker.vue";
import ReservationForm from "./common/ReservationForm.vue";
import PersonalRecap from "./personal/PersonalRecap.vue";
import ReservationServicesPage from "./service/ReservationServicesPage.vue";
import Button from "primevue/button";
import WorkersPage from "./service/WorkersPage.vue";
import CalendarConnectionPage from "./service/CalendarConnectionPage.vue";
import SelectProvider from "./common/SelectProvider.vue";
import ServiceTimeSlotPicker from "./service/ServiceTimeSlotPicker.vue";
import ServiceRecap from "./service/ServiceRecap.vue";
import SelectBlocking from "./common/SelectBlocking.vue";
import RentalSubjects from "./rental/RentalSubjects.vue";
import RentalRecap from "./rental/RentalRecap.vue";
import {router, usePage} from "@inertiajs/vue3";
import FeatureBlocker from "../../components/ui-components/FeatureBlocker.vue";
import {OverlayScrollbarsComponent as OverlayScrollbars} from "overlayscrollbars-vue";
const start = ref(false)
const previousStep = ref(0);

const features = usePage().props.user.features?.map((el) => el.feature_name);
const canSeeRental = features?.includes('roori_can_see_rental') ?? false;

const formModel = ref({
    selectedType: '',
    calendarId: '',
    calendarTimezone: null,
    calendarProvider: '',
    timeSlots: [],
    reservationType: '',
    bookingRules: [],
    settings: {
        name: '',
        language: 'cs',
        notifications: {
            newClient: {
                email: features?.includes('roori_notifications_new'),
                sms: false
            },
            confirmClient: {
                email: features?.includes('roori_notifications_accept'),
                sms: false
            },
            changeClient: {
                email: features?.includes('roori_notifications_update'),
                sms: false
            },
            cancelClient: {
                email: features?.includes('roori_notifications_cancel'),
                sms: false
            },
            reminderClient: {
                email: features?.includes('roori_notifications_reminder'),
                sms: true
            },
            //retargetingClient: features?.includes('roori_notifications_retargeting'),
        },
        description: '',
        description_code: '',
        reservation_change_settings: 'off',
        reservation_cancel_settings: 'off',
        reservation_change_auto_approve: 'N',
        reservation_cancel_auto_approve: 'N',
    },
    formTheme: {
        color: 'blue',
        borderless: false,
        transparent: false,
        darkMode: false,
        darkModeLock: false,
        background_color: 'e8e8e8',
        background_color_dark: '000000',
        background_image_url: 'https://roori.app/branding/cal-preview-light.png',
        background_image_url_dark: 'https://roori.app/branding/cal-preview-dark.png',
        logo_url: 'https://roori.app/branding/roori-logo-basic.png',
        logo_url_dark: 'https://roori.app/branding/roori-logo.png',
        panel_color: 'FFFFFF',
        panel_color_dark: '000000',
        panel_transparency: 100,
        panel_transparency_dark: 25,
        panel_backdrop_blur: false,
        panel_backdrop_blur_dark: true,
        fontFamily: 'Syne',
        rows: 1,
        columns: 1,
        roundness: 1
    },
    formSettings: {
        firstDayOfWeek: 2,
        titlePosition: 'center',
        transition: 'fade',
        locale: 'cs_CZ',
        showWeekNumbers: false,
        view: 'monthly',
    },
    workers: [],
    useMultipleWorkers: null,
    useOneCalendar: null,
    useOneTimeSlotDefinition: null,
    services: [],
    formBuilder: [],
    rentalItems: [],
    workersServices: {},
    workersCalendars: {}
})

const formTypes = reactive({
    personal: {
        name: wTrans('forms.type_personal_name'),
        disabled: false,
        icon: 'pi pi-user',
        description: wTrans('forms.type_personal_description'),
        version: 'Free',
        feature: 'roori_use_personal_form_type',
        info: wTrans('forms.type_personal_info'),
        tabs: [
            {
                header: wTrans('forms.tab_personal_calendar_header'),
                description: wTrans('forms.tab_personal_calendar_description'),
                component: markRaw(CalendarSelector),
                valid: false,
            },
            {
                header: wTrans('forms.tab_personal_availability_header'),
                component: markRaw(TimeSlotPicker),
                valid: false,
                description: wTrans('forms.tab_personal_availability_description'),
            },
            {
                header: wTrans('forms.tab_personal_form_header'),
                component: markRaw(ReservationForm),
                valid: false
            },
            /*{
                header: 'Možnosti změny',
                valid: true,
                description: 'Umožněte zákazníkům změnit, či zrušit svou rezervaci.',
                component: markRaw(CustomerChangesForm),
            },
            {
                header: 'Prvky formuláře',
                valid: true,
                description: 'Přidejte vlastní pole do formuláře',
                component: markRaw(FormBuilder),
            },
            {
                header: 'Vzhled kalendáře a formuláře',
                component: markRaw(FormTheme),
                valid: true,
                wide: true,
                command: (theme, settings) => {
                    formModel.value.formTheme = theme;
                    formModel.value.formSettings = settings
                    step.value = 5;
                }
            },*/
            {
                header: wTrans('forms.tab_launch_header'),
                component: markRaw(PersonalRecap),
                valid: true,
                last: true,
            }
        ]
    },
    service: {
        name: wTrans('forms.type_service_name'),
        icon: 'pi pi-briefcase',
        description: wTrans('forms.type_service_description'),
        version: 'Pro',
        feature: 'roori_use_service_form_type',
        info: wTrans('forms.type_service_info'),
        tabs: [
            {
                header: wTrans('forms.tab_service_services_header'),
                description: wTrans('forms.tab_service_services_description'),
                valid: false,
                component: markRaw(ReservationServicesPage),
            },
            {
                header: wTrans('forms.tab_service_workers_header'),
                valid: false,
                description: wTrans('forms.tab_service_workers_description'),
                component: markRaw(WorkersPage)
            },
            {
                header: wTrans('forms.tab_provider_header'),
                valid: false,
                description: wTrans('forms.tab_provider_description'),
                component: markRaw(SelectProvider)
            },
            {
                header: wTrans('forms.tab_service_calendar_header'),
                valid: false,
                description: wTrans('forms.tab_service_calendar_description'),
                component: markRaw(CalendarConnectionPage)
            },
            {
                header: wTrans('forms.tab_service_timeslots_header'),
                valid: false,
                description: wTrans('forms.tab_service_timeslots_description'),
                component: markRaw(ServiceTimeSlotPicker),
            },
            {
                header: wTrans('forms.tab_form_header'),
                valid: false,
                description: wTrans('forms.tab_service_form_description'),
                component: markRaw(ReservationForm),
            },
            /*{
                header: 'Možnosti změny',
                valid: true,
                description: 'Umožněte zákazníkům změnit, či zrušit svou rezervaci.',
                component: markRaw(CustomerChangesForm),
            },
            {
                header: 'Prvky formuláře',
                valid: true,
                description: 'Přidejte vlastní pole do formuláře',
                component: markRaw(FormBuilder),
            },
            {
                header: 'Vzhled kalendáře a formuláře',
                component: markRaw(FormTheme),
                valid: true,
                wide: true,
            },*/
            {
                header: wTrans('forms.tab_launch_header'),
                component: markRaw(ServiceRecap),
                valid: true,
                last: true,
            }
        ]
    },
    rental: {
        name: wTrans('forms.type_rental_name'),
        icon: 'pi pi-box',
        description: wTrans('forms.type_rental_description'),
        disabled: false,
        version: 'Standard',
        feature: 'roori_can_use_rental',
        requiresVisibility: true,
        info: wTrans('forms.type_rental_info'),
        tabs: [
            {
                header: wTrans('forms.tab_rental_blocking_header'),
                valid: false,
                description: wTrans('forms.tab_rental_blocking_description'),
                component: markRaw(SelectBlocking),
            },
            {
                header: wTrans('forms.tab_provider_header'),
                valid: false,
                description: wTrans('forms.tab_provider_description'),
                component: markRaw(SelectProvider),
            },
            {
                header: wTrans('forms.tab_rental_subjects_header'),
                valid: false,
                description: wTrans('forms.tab_rental_subjects_description'),
                component: markRaw(RentalSubjects),
            },
            {
                header: wTrans('forms.tab_form_header'),
                valid: false,
                description: wTrans('forms.tab_form_description'),
                component: markRaw(ReservationForm),
            },
            {
                header: wTrans('forms.tab_launch_header'),
                component: markRaw(RentalRecap),
                valid: true,
                last: true,
            },
        ],
    },
    accommodation: {
        name: wTrans('forms.type_accommodation_name'),
        icon: 'pi pi-home',
        description: wTrans('forms.type_accommodation_description'),
        disabled: false,
        version: 'Standard',
        feature: 'roori_can_use_rental',
        requiresVisibility: true,
        info: wTrans('forms.type_accommodation_info'),
        tabs: [
            {
                header: wTrans('forms.tab_provider_header'),
                valid: false,
                description: wTrans('forms.tab_provider_description'),
                component: markRaw(SelectProvider),
            },
            {
                header: wTrans('forms.tab_accommodation_units_header'),
                valid: false,
                description: wTrans('forms.tab_accommodation_units_description'),
                component: markRaw(RentalSubjects),
            },
            {
                header: wTrans('forms.tab_form_header'),
                valid: false,
                description: wTrans('forms.tab_form_description'),
                component: markRaw(ReservationForm),
            },
            {
                header: wTrans('forms.tab_launch_header'),
                component: markRaw(RentalRecap),
                valid: true,
                last: true,
            },
        ],
    },
});
const step = ref(-1);

const currentFormType = computed(() => {
    if (formModel.value.selectedType)
        return formTypes[formModel.value.selectedType];
    return [];
});

const lastStep = computed(() => {
    if (formModel.value.selectedType) {
        return currentFormType.value?.tabs.length
    }
    return 3;
});

const shouldBeContainer = computed(() => {
    if (formModel.value.selectedType) {
        return !currentFormType.value?.tabs[step.value - 1]?.wide;
    }
    return true;
});

watch(step, (newStep, oldStep) => {
    previousStep.value = oldStep;
});

watch(() => formModel.value.selectedType, (type) => {
    if (type === 'accommodation') {
        formModel.value.reservationType = 'multi-day';
    }
});
</script>
<template>
    <transition-group :name="step > previousStep ? 'slide-fwd' : 'slide-back'">
    <div :key="step" class="wizard-container absolute top-0 bottom-0 left-0 max-md:top-12 max-md:bottom-12 px-4">
            <div class="relative flex flex-col justify-center h-full py-4"
                 style="min-height: calc(100dvh - 160px);">

                <OverlayScrollbars element="div" :class="shouldBeContainer ? 'container max-w-[1200px]' : ''"
                                   class="px-1">

                    <div v-if="step === -1" id="first-page" class="flex flex-col items-start gap-4">
                        <h1 class="text-2xl font-bold">{{ $t('forms.wizard_greeting') }} 👋</h1>
                        <p class="text-lg">{{ $t('forms.wizard_intro') }}</p>
                        <p class="text-lg">{{ $t('forms.wizard_ready') }}</p>

                        <Button @click="step++" :label="$t('forms.wizard_start_btn')" icon="pi pi-arrow-right" icon-pos="right" rounded
                                icon-class="ml-2"></Button>
                    </div>

                    <div v-else-if="step === 0" id="second-page" class="flex flex-col items-start gap-2">
                        <h1 class="text-2xl font-bold">{{ $t('forms.wizard_type_title') }}</h1>
                        <p class="text-pretty md:text-lg">{{ $t('forms.wizard_type_description') }}</p>

                        <div class="grid gap-3 py-8">
                            <template v-for="(type, i) in formTypes" :key="type.name">
                                <template v-if="!type.requiresVisibility || canSeeRental">
                                <feature-blocker :name="type.feature" :version="type.version" inline>
                                    <div
                                        class="flex gap-4 bg-white dark:bg-black dark:border-neutral-700 p-4 rounded-lg shadow-sm border relative items-center cursor-pointer hover:bg-neutral-100 hover:dark:bg-gray-900 transition-all"
                                        :class="{
                                '!bg-neutral-100': type.disabled,
                                'ring-blue-400 dark:ring-blue-800 ring-2 ring-offset-2 dark:ring-offset-0': formModel.selectedType === i,
                             }"
                                        @click="formModel.selectedType = (formModel.selectedType === i || type.disabled) ? null : i"

                                    >
                                        <div class="p-2 bg-gray-200 dark:bg-gray-800 rounded" :class="type.disabled ? 'opacity-40':''">
                                            <i :class="type.icon"></i>
                                        </div>
                                        <div class="flex flex-col gap-0 grow" :class="type.disabled ? 'opacity-40':''">
                                            <p class="font-bold text-sm">{{ type.name }}</p>
                                            <p class="text-sm opacity-70">{{ type.description }}</p>
                                        </div>
                                        <i class="pi pi-info-circle mb-5 opacity-50" v-tooltip="type.info"></i>

                                        <div v-if="type.disabled" class="absolute h-full left-0 w-full flex justify-center items-center">
                                            <p class="bg-yellow-500 p-2 px-4 rounded shadow-xl text-white -rotate-12">{{ $t('forms.wizard_coming_soon') }}</p>
                                        </div>
                                    </div>
                                </feature-blocker>
                                </template>
                            </template>
                        </div>


                    </div>

                    <template v-for="(type, i) in currentFormType?.tabs" :key="type.header">
                        <div v-if="step === i + 1" class="dark:bg-transparent flex flex-col gap-2">
                            <h1 class="text-xl md:text-2xl font-bold">{{ type.header }}</h1>
                            <p class=" md:text-lg">{{ type.description }}</p>
                            <component :is="type.component" class="py-8" v-model:start="start" v-model="formModel"
                                       v-model:valid="type.valid" :key="`slide-${i}`"
                                       @done="type.command"></component>
                        </div>
                    </template>


                    <div v-if="step !== -1" class="flex gap-2 button-bar  max-md:pb-8">
                        <Button @click="step--" :label="$t('forms.wizard_back')" icon="pi pi-arrow-left" icon-pos="left"
                                severity="secondary" outlined rounded icon-class="ml-2"></Button>
                        <Button v-if="lastStep !== step" @click="step++" :label="$t('forms.wizard_next')"
                                :disabled="(step === 0 && !formModel.selectedType) || (step !== 0 && (!currentFormType?.tabs[step-1].valid ?? false))"
                                rounded icon-class="ml-2"></Button>

                        <Button v-if="lastStep === step" @click="start = true" :label="$t('forms.wizard_launch')" rounded
                                icon-class="ml-2"></Button>
                    </div>

                </OverlayScrollbars>

            </div>

    </div>

    </transition-group>
</template>

<style scoped lang="scss">

.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
    position: absolute;
}

.fade-enter-from {
    opacity: 0;
    translate: -100px 0;
}

.fade-enter-to {
    opacity: 1;
    translate: 0 0;
}

.fade-leave-from {
    opacity: 1;
    translate: 0 0;
}

.fade-leave-to {
    opacity: 0;
    translate: 100px 0;
}

.slide-fwd-enter-active,
.slide-fwd-leave-active {
    transition: all 0.3s ease;
    position: absolute;
    width: 100%;
}

.slide-fwd-enter-from {
    transform: translateX(25%);
    opacity: 0;
}

.slide-fwd-enter-to {
    transform: translateX(0);
    opacity: 1;
}

.slide-fwd-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.slide-fwd-leave-to {
    transform: translateX(-25%);
    opacity: 0;
}

.slide-back-enter-active,
.slide-back-leave-active {
    transition: all 0.3s ease;
    position: absolute;
    width: 100%;
}

.slide-back-enter-from {
    transform: translateX(-25%);
    opacity: 0;
}

.slide-back-enter-to {
    transform: translateX(0);
    opacity: 1;
}

.slide-back-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.slide-back-leave-to {
    transform: translateX(25%);
    opacity: 0;
}

.wizard-container
{
    width: 100%;
}
</style>
